import { Box, Stack, Wrap, WrapItem } from '@chakra-ui/react';
import { type FC, useEffect, useMemo, useState } from 'react';
import SearchInput from '@/components/Atoms/FaceLiftSearchInput';
import { FaceliftAccordion } from '@/components/Atoms/FaceliftAccordion';
import { FaceliftTag } from '@/components/Atoms/FaceliftTag';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import type { IItem } from './FiltersDrawer';

export type ISelectedItem = {
	key: string;
	name: string;
	options: { label: string; id: string | number }[];
};
interface IDrawerContentProps {
	items: IItem[];
	defaultValues?: ISelectedItem[];
	showSearch?: boolean;
	onChange?: (selected: ISelectedItem[]) => void;
}

const DrawerContent: FC<IDrawerContentProps> = ({
	items,
	defaultValues = [],
	showSearch = false,
	onChange,
}) => {
	const [selectedItems, setSelectedItems] = useState<ISelectedItem[]>([]);
	const [searchTerm, setSearchTerm] = useState('');

	const handleTagClose = (categoryName: string, id: number | string) => {
		setSelectedItems((prev) =>
			prev.map((cat) =>
				cat.name === categoryName
					? { ...cat, options: cat.options.filter((opt) => opt.id !== id) }
					: cat,
			),
		);
	};

	// Notify parent whenever selection changes
	useEffect(() => {
		if (onChange) {
			onChange(selectedItems);
		}
	}, [selectedItems, onChange]);

	useMemo(() => {
		setSelectedItems(defaultValues);
	}, [defaultValues]);

	const filteredItems = useMemo(() => {
		if (!searchTerm.trim()) return items;

		return items.map((item) => {
			if (item.name === 'Clinics') {
				const filteredOptions = item.options?.filter((opt: any) =>
					opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
				);
				return { ...item, options: filteredOptions };
			}
			return item;
		});
	}, [items, searchTerm]);

	const filteredTags = useMemo(() => {
		if (!searchTerm.trim()) return selectedItems;
		return selectedItems.map((cat) => {
			if (cat.name === 'Clinics') {
				const filteredOptions = cat.options.filter((opt) =>
					opt.label.toLowerCase().includes(searchTerm.toLowerCase()),
				);
				return { ...cat, options: filteredOptions };
			}
			return cat;
		});
	}, [selectedItems, searchTerm]);

	return (
		<Stack px={1} height={'full'} overflowY={'scroll'}>
			<Stack padding="0 14px" marginTop="10px" marginBottom="5px">
				<Show when={selectedItems.map((cat) => cat.options.length).reduce((a, b) => a + b, 0) > 0}>
					<FaceliftText fontSize="sm" fontWeight="semibold" marginBottom="1px">
						Active Filters
					</FaceliftText>
				</Show>
				<Wrap gap={3}>
					{filteredTags.map((category, catIndex) =>
						category.options.map((option, index) => (
							<WrapItem key={option.id}>
								<FaceliftTag
									label={option.label}
									tabIndex={index}
									colorIndex={catIndex}
									closable
									onClose={() => handleTagClose(category.name, option.id)}
								/>
							</WrapItem>
						)),
					)}
				</Wrap>
			</Stack>

			<Show when={showSearch}>
				<Box padding={'0 14px'} marginBottom={'10px'}>
					<SearchInput
						placeholder='Search By Clinic'
						sx={{ borderBottom: '1px solid', borderColor: 'gray.300' }}
						inputProps={{
							borderRadius: '0',
							ml: '-4',
							border: 'none',
							_focus: {
								outline: 'none',
								boxShadow: 'none',
								borderColor: 'gray.400',
							},
						}}
						startElementProps={{
							padding: '0',
						}}
						onChange={(e) => setSearchTerm(e.target.value)}
						onClear={() => setSearchTerm('')}
					/>
				</Box>
			</Show>

			<FaceliftAccordion
				items={filteredItems}
				onChange={(selected) => setSelectedItems(selected)}
				defaultValues={selectedItems}
			/>
		</Stack>
	);
};

export default DrawerContent;
